import { getResult } from "../../core/helpers/functional-util.js";
import DefaultTextAction from "./actions/DefaultTextAction.js";
import getAccountExtraSpecs from "./builders/getAccountExtraSpec.js";
import getAccountInputValueSpec from "./builders/getAccountInputValueSpec.js";
import getKeyFigureExtraSpecs from "./builders/getKeyFigureExtraSpecs.js";
import getKeyFigureValueSpec from "./builders/getKeyFigureValueSpec.js";
import getPeriodSpec from "./builders/getPeriodSpec.js";
import InputValueAction from "./actions/InputValueAction/InputValueAction.js";
import InsertPeriodAction from "./actions/InsertPeriodAction.js";

/**
 * Class generating InputValueAction instances and related actions.
 * This class has two very important fields, `inputValueActionSpecs` and `extraActionSpecs`.
 * `inputValueActionSpecs` holds a list of InputValueActionSpec, each with a different type.
 * `extraActionSpecs` is a Map where the key is a input value type (i.e. 'keyfigure') and the value
 * is an array of Action makers (i.e. function resulting in an Action). The value can also be a
 * function to allow late binding.
 *
 * @param {function(object): void} [makePeriodPopover] - Function used to create a popover for period input values
 * @param {function(object): void} [makeKeyFigurePopover] - Function used to create a popover for keyfigure input values
 * @param {function(object): void} [makeAccountPopover] - Function used to create a popover for account input values
 * @param {KeyFigureCalculationService} keyFigureCalculationService - Service used to calculate
 *   a keyFigure value
 * @param {function} getPeriod - Function returning the base period used to fetch data
 * @param {function():Promise} getPeriodReportValuesKeyFigurePreset - Function returning a
 *   Promise that will resolve to the keyFigure preset that will be
 *   rendered for the keyFigure input value feature
 * @param {function():Account[]} getAllAccounts - Function returning all the available accounts
 *   for the current fiscal year
 * @param {PeriodService} periodService - Service used to retrieve a period
 */
export default class InputValueActionBuilder {
	constructor({
		makePeriodPopover,
		makeKeyFigurePopover,
		makeAccountPopover,
		keyFigureCalculationService,
		getAccountByNumber,
		getPeriod,
		getPeriodReportValuesKeyFigurePreset,
		getAllAccounts,
		periodService,
	}) {
		this._inputValueActionSpecs = [
			getKeyFigureValueSpec({
				makeKeyFigurePopover,
				keyFigureCalculationService,
				getPeriod,
				getPeriodReportValuesKeyFigurePreset,
			}),
			getAccountInputValueSpec({
				makeAccountPopover,
				keyFigureCalculationService,
				getPeriod,
				getAccountByNumber,
			}),
			getPeriodSpec({ makePeriodPopover, getPeriod, periodService }),
		];

		this._extraActionSpecs = {
			period: [(...args) => new InsertPeriodAction(...args)],
			keyfigure: () =>
				getKeyFigureExtraSpecs({
					getPeriodReportValuesKeyFigurePreset,
				}),
			account: () =>
				getAccountExtraSpecs({
					getAllAccounts,
				}),
			defaultTexts: [
				({ defaultTexts }) => new DefaultTextAction({ defaultTexts }),
			],
		};
	}

	/**
	 * Build all the InputValueAction instances. If `types` is provided,
	 * it will only build the instances for those types.
	 *
	 * @param {string[]} [types] - Types of the InputValueAction to build
	 * @param {object} [extraParameters] - Any extra parameters to pass to the Action constructor
	 * @return {InputValueAction[]}
	 */
	build({ types, extraParameters } = {}) {
		return [
			...this._buildActions({ types, extraParameters }),
			...this._buildExtraActions({ types, extraParameters }),
		];
	}

	_buildActions({ types, extraParameters }) {
		const actionSpecs = this._filterSpecsByType({ types });

		return actionSpecs.map((spec) =>
			this._makeInputValueAction({
				...spec,
				...extraParameters,
			}),
		);
	}

	_buildExtraActions({ types, extraParameters }) {
		return this._filterExtraSpecsByType({ types }).map((fn) =>
			fn({ ...extraParameters }),
		);
	}

	_filterSpecsByType({ types }) {
		if (!types) {
			return this._inputValueActionSpecs;
		}
		return this._inputValueActionSpecs.filter((actionSpec) =>
			types.includes(actionSpec.type),
		);
	}

	_filterExtraSpecsByType({ types }) {
		if (!types)
			return Object.values(this._extraActionSpecs).flatMap((spec) =>
				getResult(spec),
			);

		return types.flatMap((type) =>
			getResult(this._extraActionSpecs[type] || []),
		);
	}

	_makeInputValueAction(spec) {
		return new InputValueAction(spec);
	}
}
